import React, { useState } from "react";
import {
  Box,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import ReferralStatusChip from "../../common/ReferralStatusChip";
import { TOUCH_QUERY } from "../../../utils/useViewport";
import {
  ActionItemDueState,
  FeedItem,
  dueTodayChipSx,
  feedTypeMeta,
  overdueChipSx,
  referralDaysInPipeline,
  touchMinTextSx,
  upcomingChipSx,
} from "./tokens";

interface FeedRowProps {
  item: FeedItem;
  onComplete: (id: string) => void;
  onSnooze: (id: string, days: number) => void;
  onEdit: (reminder: any) => void;
  onDelete: (id: string) => void;
}

const pillSx = {
  padding: "3px 9px",
  borderRadius: "12px",
  fontSize: "11.5px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  ...touchMinTextSx,
};

const iconButtonSx = {
  width: 28,
  height: 28,
  borderRadius: "7px",
  [`@media ${TOUCH_QUERY}`]: { width: 40, height: 40 },
};

const captionSx = { fontSize: "11.5px", color: "#8a8a90", ...touchMinTextSx };

// "2h overdue" / "in 3h" — the prototype's compact form, which moment's
// fromNow() ("2 hours ago") doesn't give us.
const formatDue = (dueAt: any) => {
  const due = moment(dueAt);
  const now = moment();
  const overdue = due.isBefore(now);
  const minutes = Math.abs(now.diff(due, "minutes"));
  let value = Math.max(1, minutes);
  let unit = "m";
  if (minutes >= 60 * 24) {
    value = Math.round(minutes / (60 * 24));
    unit = "d";
  } else if (minutes >= 60) {
    value = Math.round(minutes / 60);
    unit = "h";
  }
  return {
    overdue,
    label: overdue ? `${value}${unit} overdue` : `in ${value}${unit}`,
  };
};

// The action-item lane now includes future-dated items, so the due chip has
// three presentations: Overdue (red), Due today (amber) and a neutral
// "Due Mar 4" for anything still upcoming.
const actionItemDueChip = (
  dueState: ActionItemDueState | undefined,
  nextDue: any,
) => {
  if (dueState === "overdue") return { sx: overdueChipSx, label: "Overdue" };
  if (dueState === "today") return { sx: dueTodayChipSx, label: "Due today" };
  const due = nextDue ? moment(nextDue) : null;
  if (!due || !due.isValid()) return { sx: upcomingChipSx, label: "Upcoming" };
  const format = due.year() === moment().year() ? "MMM D" : "MMM D, YYYY";
  return { sx: upcomingChipSx, label: `Due ${due.format(format)}` };
};

const FeedRow: React.FC<FeedRowProps> = ({
  item,
  onComplete,
  onSnooze,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();
  const [snoozeAnchor, setSnoozeAnchor] = useState<null | HTMLElement>(null);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  const { type, raw } = item;
  const meta = feedTypeMeta[type];
  const Icon = meta.icon;

  let title = "";
  let subtitle = "";
  let href: string | null = null;
  let widget: React.ReactNode = null;

  if (type === "reminders") {
    const due = formatDue(raw.dueAt);
    title = raw.title;
    subtitle = raw.firm_name || "Personal reminder";
    href = raw.case_id
      ? `/case?id=${raw.case_id}&reminder=${raw._id}`
      : null;
    widget = (
      <>
        <Box
          component="span"
          sx={{ ...pillSx, ...(due.overdue ? overdueChipSx : upcomingChipSx) }}
        >
          {due.label}
        </Box>
        <Box sx={{ display: "flex", gap: "2px" }}>
          <Tooltip title="Complete">
            <IconButton
              size="small"
              sx={{ ...iconButtonSx, "&:hover": { backgroundColor: "#eafaf0" } }}
              onClick={(e) => {
                e.stopPropagation();
                onComplete(String(raw._id));
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 18, color: "#2e7d32" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Snooze">
            <IconButton
              size="small"
              sx={{ ...iconButtonSx, "&:hover": { backgroundColor: "#eef2f6" } }}
              onClick={(e) => {
                e.stopPropagation();
                setSnoozeAnchor(e.currentTarget);
              }}
            >
              <ScheduleIcon sx={{ fontSize: 18, color: "#5a6b7a" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="More">
            <IconButton
              size="small"
              sx={{ ...iconButtonSx, "&:hover": { backgroundColor: "#eef2f6" } }}
              onClick={(e) => {
                e.stopPropagation();
                setMoreAnchor(e.currentTarget);
              }}
            >
              <MoreVertIcon sx={{ fontSize: 18, color: "#5a6b7a" }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Menu
          anchorEl={snoozeAnchor}
          open={Boolean(snoozeAnchor)}
          onClose={() => setSnoozeAnchor(null)}
        >
          {[1, 3, 7].map((days) => (
            <MenuItem
              key={days}
              onClick={() => {
                setSnoozeAnchor(null);
                onSnooze(String(raw._id), days);
              }}
            >
              {days} day{days > 1 ? "s" : ""}
            </MenuItem>
          ))}
        </Menu>
        <Menu
          anchorEl={moreAnchor}
          open={Boolean(moreAnchor)}
          onClose={() => setMoreAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMoreAnchor(null);
              onEdit(raw);
            }}
          >
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMoreAnchor(null);
              onDelete(String(raw._id));
            }}
          >
            Delete
          </MenuItem>
        </Menu>
      </>
    );
  } else if (type === "referrals") {
    const age = referralDaysInPipeline(raw);
    title = raw.firm_name || "Referral";
    subtitle = raw.referred_to ? `Referral → ${raw.referred_to}` : "Referral";
    href = `/case?id=${raw.case_id}${
      raw._id ? `&referral=${raw._id}` : ""
    }`;
    widget = (
      <>
        <ReferralStatusChip status={raw.pipeline_status} />
        <Typography sx={captionSx}>{age}d in pipeline</Typography>
      </>
    );
  } else if (type === "plans") {
    const total = raw.tasks_total || 0;
    const done = raw.tasks_completed || 0;
    const caseId = raw.case?._id;
    title = raw.firm?.["Company Name"] || "Action item";
    subtitle = raw.goal || "Action item";
    href = caseId
      ? `/case?id=${caseId}${raw._id ? `&action_item=${raw._id}` : ""}`
      : null;
    const dueChip = actionItemDueChip(raw.dueState, raw.nextDue);
    widget = (
      <>
        <Box component="span" sx={{ ...pillSx, ...dueChip.sx }}>
          {dueChip.label}
        </Box>
        {total > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              maxWidth: 260,
            }}
          >
            <LinearProgress
              variant="determinate"
              value={(done / total) * 100}
              sx={{
                flexGrow: 1,
                minWidth: 90,
                height: 6,
                borderRadius: "3px",
                backgroundColor: "#ececef",
                "& .MuiLinearProgress-bar": { backgroundColor: "#0F95B3" },
              }}
            />
            <Typography sx={{ ...captionSx, whiteSpace: "nowrap" }}>
              {done}/{total} tasks
            </Typography>
          </Box>
        )}
      </>
    );
  } else if (type === "inactive") {
    title = raw.firm_name || "Client";
    subtitle = "No recent interaction";
    href = `/case?id=${raw.case_id}`;
    widget = (
      <Box component="span" sx={{ ...pillSx, ...dueTodayChipSx }}>
        {raw.daysInactive === null
          ? "No interaction"
          : `${raw.daysInactive}d inactive`}
      </Box>
    );
  } else {
    title = raw.company || "Web intake";
    subtitle = "New web intake — unassigned";
    href = "/dashboard/webIntakeForms";
    widget = (
      <>
        <Box
          component="span"
          sx={{
            ...pillSx,
            fontWeight: 700,
            backgroundColor: "#e8edf3",
            color: "#3F5D7D",
          }}
        >
          NEW
        </Box>
        {raw.submitted && (
          <Typography sx={captionSx}>
            {moment(raw.submitted).fromNow()}
          </Typography>
        )}
      </>
    );
  }

  return (
    <Box
      onClick={() => href && navigate(href)}
      sx={{
        display: "flex",
        gap: "12px",
        alignItems: "flex-start",
        padding: "13px 16px",
        borderBottom: "1px solid #f4f4f6",
        borderLeft: `4px solid ${meta.accent}`,
        cursor: href ? "pointer" : "default",
        "&:hover": { backgroundColor: "#faf8fa" },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: "9px",
          backgroundColor: meta.iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon sx={{ fontSize: 19, color: meta.accent }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: "14.5px", fontWeight: 700, color: "#26262b" }}
        >
          {title}
        </Typography>
        <Typography noWrap sx={{ fontSize: "12.5px", color: "#7c7c82" }}>
          {subtitle}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "6px",
          flexShrink: 0,
        }}
      >
        {widget}
      </Box>
    </Box>
  );
};

export default FeedRow;
